import { useRef } from 'react';
import { Check, GripHorizontal, ListChecks, Plus, Trash2, X } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { stickyPalette } from '../constants';
import type { ChecklistItem, StickyElement } from '../types';

interface StickyNoteProps {
  element: StickyElement;
  canEdit: boolean;
  onChange: (patch: Partial<StickyElement>) => void;
  onDelete: () => void;
  /** Bounds of the canvas so a note can't be dragged off-screen. */
  canvasRef: React.RefObject<HTMLDivElement | null>;
}

const rid = () => Math.random().toString(36).slice(2, 9);

/**
 * A single draggable colored sticky note. Carries free text and/or a checklist.
 * Position (x/y) is reported up via onChange so the parent persists it into the
 * note's `elements` array. Read-only notes render without drag/edit affordances.
 */
export function StickyNote({ element, canEdit, onChange, onDelete, canvasRef }: StickyNoteProps) {
  const pal = stickyPalette(element.color);
  const drag = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!canEdit) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startY: e.clientY, originX: element.x, originY: element.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    const bounds = canvasRef.current?.getBoundingClientRect();
    const maxX = (bounds?.width ?? 4000) - element.w;
    const maxY = (bounds?.height ?? 4000) - 40;
    const nx = Math.max(0, Math.min(maxX, drag.current.originX + dx));
    const ny = Math.max(0, Math.min(maxY, drag.current.originY + dy));
    onChange({ x: nx, y: ny });
  };
  const onPointerUp = () => { drag.current = null; };

  const items = element.items;
  const setItems = (next: ChecklistItem[]) => onChange({ items: next });

  return (
    <div
      className="absolute select-none"
      style={{ left: element.x, top: element.y, width: element.w }}
    >
      <div
        className="rounded-[3px] shadow-md"
        style={{ background: pal.bg, border: `1px solid ${pal.border}` }}
      >
        {/* Washi-tape accent + drag handle */}
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className={cn('flex items-center justify-between px-2 py-1', canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-default')}
          style={{ background: pal.tape }}
        >
          <GripHorizontal className="h-3.5 w-3.5 opacity-50" />
          {canEdit && (
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                aria-label="Toggle checklist"
                title="Toggle checklist"
                onClick={() => setItems(items ? [] : [{ id: rid(), text: '', done: false }])}
                className="rounded p-0.5 text-black/50 hover:bg-black/10"
              >
                <ListChecks className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                aria-label="Delete note"
                title="Delete note"
                onClick={onDelete}
                className="rounded p-0.5 text-black/50 hover:bg-black/10"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        <div className="p-2.5">
          <textarea
            value={element.text}
            disabled={!canEdit}
            onChange={(e) => onChange({ text: e.target.value })}
            placeholder={canEdit ? 'Write…' : ''}
            className="w-full resize-none border-0 bg-transparent text-body-sm leading-6 text-neutral-800 placeholder:text-black/30 focus:outline-none"
            rows={items ? 2 : 5}
          />

          {items && (
            <ul className="mt-1 space-y-1">
              {items.map((it) => (
                <li key={it.id} className="flex items-start gap-1.5">
                  <button
                    type="button"
                    aria-label={it.done ? 'Mark not done' : 'Mark done'}
                    disabled={!canEdit}
                    onClick={() => setItems(items.map((x) => (x.id === it.id ? { ...x, done: !x.done } : x)))}
                    className={cn(
                      'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      it.done ? 'bg-neutral-800 text-white' : 'border-black/40 bg-white/60',
                    )}
                  >
                    {it.done && <Check className="h-3 w-3" />}
                  </button>
                  <input
                    value={it.text}
                    disabled={!canEdit}
                    onChange={(e) => setItems(items.map((x) => (x.id === it.id ? { ...x, text: e.target.value } : x)))}
                    placeholder="List item"
                    className={cn(
                      'flex-1 border-0 bg-transparent text-body-sm text-neutral-800 placeholder:text-black/30 focus:outline-none',
                      it.done && 'text-black/40 line-through',
                    )}
                  />
                  {canEdit && (
                    <button
                      type="button"
                      aria-label="Remove item"
                      onClick={() => setItems(items.filter((x) => x.id !== it.id))}
                      className="mt-0.5 rounded p-0.5 text-black/30 hover:bg-black/10 hover:text-black/60"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </li>
              ))}
              {canEdit && (
                <li>
                  <button
                    type="button"
                    onClick={() => setItems([...items, { id: rid(), text: '', done: false }])}
                    className="inline-flex items-center gap-1 text-body-sm text-black/50 hover:text-black/80"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add item
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
